import { ModalContext } from "app/app";
import axios from "axios";
import { IconedButton } from "components/IconedButton";
import ResponsiveTable from "components/ResponsiveTable";
import {
  Animate,
  AuthContext,
  Loader,
  ViewContext,
  useNavigate,
} from "components/lib";
import DateHelper from "helpers/dateUtils";
import { useContext, useEffect, useState } from "react";
import { Providers } from "../../helpers/constants";
import { quoteColumns } from "./columns";
import { Eye, Edit, Trash2, Folder } from "feather-icons-react/build/IconComponents";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useRef } from "react";
import { useCallback } from "react";
import { getLatestSubmissionNumber } from "helpers/submission";

export function Quote() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const context = useContext(ViewContext);
  const { setModal } = useContext(ModalContext);

  async function listQuotes() {
    try {
      setLoading(true);

      const params = {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: debouncedFilter || undefined,
        agency_id: auth.user.account_id,
      };

      if (sorting.length > 0) {
        params.sortBy = sorting[0].id;
        params.sortOrder = sorting[0].desc ? 'desc' : 'asc';
      }

      const res = await axios.get('/api/quote', { params });

      const format = new DateHelper();
      const timezone = await format.getTimezone();

      const quotesData = res.data.data.data.map(value => ({
        id: value.id,
        submission_number: getLatestSubmissionNumber(value),
        "DOT Number": value.dot_number,
        company: value.company_name,
        company_id: value.company_id,
        policy_type: value.policy_type,
        effective_date: format.isoDate(value.effective_date),
        producer: value.uploaded_by,
        carrier: value.carrier,
        premium: value.premium ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(value.premium) : "$0.00",
        Drivers: value.number_of_drivers,
        Vehicles: value.number_of_vehicles,
        Trailers: value.number_of_trailers,
        "Created At": format.timestamp(value.createdAt, timezone),
        status: value.status.replaceAll("pending_details", "processing").replaceAll("_", " "),
        quote: value,
      }));

      setData(quotesData);
      setPagination({
        ...pagination,
        pageIndex: res.data.data.page - 1,
        totalItems: res.data.data.rows,
        totalPages: res.data.data.pages_count,
      });

      setLoading(false);
    } catch (error) {
      setLoading(false);
      context.notification.show("Failed to load table data", "error", true, "toast", "x-circle");
    }
  }

  const handleViewQuote = (row) => {
    const reviewUrl = '/app/quote/review';

    navigate(reviewUrl, {
      state: {
        data: {
          ...row.quote,
        },
      },
    });
  };

  const handleEditQuote = (row) => {
    const editUrl = '/app/quote/run';

    navigate(editUrl, {
      state: {
        data: {
          company_id: row.company_id,
          quote_id: row.id,
        },
      },
    });
  };

  const handleDeleteQuote = (quote) => {
    setModal({
      name: "confirmation",
      data: {
        title: `Delete quote`,
        description: `Do you really want to delete this quote?`,
      },
      action: async () => {
        setModal("");
        setLoading(true);

        await axios
          .delete(`/api/quote/${quote.id}`)
          .then(() => {
            listQuotes();
          })
          .catch(() => {
            setLoading(false);
            context.notification.show(
              "Failed to load table data",
              "error",
              true,
              "toast",
              "x-circle"
            );
          });
      },
    });
  };

  const handleViewDocuments = (quote) => {
    setModal({
      name: "quote_documents_modal",
      data: {
        quoteId: quote.id,
        companyId: quote.company_id,
        submissionNumber: quote.submission_number,
        companyName: quote.company_name,
      },
    });
  };

  const columnsWithActions = [
    ...quoteColumns,
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewQuote(row.original)}
            className={`p-1 rounded hover:bg-gray-200 ${row.original.status === 'created' ? 'opacity-50 cursor-not-allowed' : ''}`}
            title="Review this quote"
            disabled={row.original.status === 'created'}
          >
            <Eye size={16} />
          </button>
          {!(row.original.policy_type === "personal_auto" || row.original.policy_type === "renters") && (
            <button
              onClick={() => handleEditQuote(row.original)}
              className="p-1 rounded hover:bg-gray-200"
              title="Edit this quote"
            >
              <Edit size={16} color="#637381" />
            </button>
          )}
          <button
            onClick={() => handleViewDocuments(row.original)}
            className="p-1 rounded hover:bg-gray-200"
            title="View uploaded documents"
          >
            <Folder size={16} color="#637381" />
          </button>
          <button
            onClick={() => handleDeleteQuote(row.original)}
            className="p-1 rounded hover:bg-gray-200"
            title="Delete quote"
          >
            <Trash2 size={16} color="#637381" />
          </button>
        </div>
      ),
    },
  ];

  const debounceTimeout = useRef(null);

  const debounceSearch = useCallback((searchValue) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setDebouncedFilter(searchValue);

      if (searchValue) {
        setPagination(prev => ({
          ...prev,
          pageIndex: 0
        }));
      }
    }, 500);
  }, []);

  const handleGlobalFilterChange = (value) => {
    setGlobalFilter(value);
    debounceSearch(value);
  };

  const table = useReactTable({
    data,
    columns: columnsWithActions,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      rowSelection,
      pagination,
    },
    initialState: {
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
    },
    // Pagination
    onPaginationChange: setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    // Column
    onColumnFiltersChange: setColumnFilters,
    // Row
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    // Sort and Filter
    onSortingChange: setSorting,
    // onGlobalFilterChange: setGlobalFilter,
    onGlobalFilterChange: handleGlobalFilterChange,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    pageCount: pagination.totalPages,
    manualSorting: true,
    manualFiltering: true,
  });

  useEffect(() => {
    listQuotes();
  }, [pagination.pageIndex, pagination.pageSize, debouncedFilter, sorting]);

  return (
    <Animate type="pop">
      <>
        {loading ? (
          <Loader />
        ) : (
          <>
            <div className="flex justify-end mb-4 gap-3">
              <IconedButton
                size="220px"
                color="#d32425"
                textColor="white"
                icon="plus-circle"
                text="Run Commercial Quote"
                action={() =>
                  setModal({
                    name: "run_quote_modal",
                  })
                }
              />
              <IconedButton
                size="220px"
                color="#1d4ed8"
                textColor="white"
                icon="plus-circle"
                text="Run Personal Quote"
                action={() =>
                  navigate("/app/quote/individual", {
                    state: { data: { quoteType: "personal_auto" } },
                  })
                }
              />
            </div>
            <div>
              <ResponsiveTable
                table={table}
                globalFilter={globalFilter}
                setGlobalFilter={handleGlobalFilterChange}
                loading={loading}
                hasFooter={true}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
              />
            </div>
          </>
        )}
      </>
    </Animate>
  );
}
